import React, { useState } from 'react';
import Papa from 'papaparse';
import {
  detectEmailKey,
  detectEntriesKey,
  prepareCsvSourceForParsing,
} from '../utils/winnerSelectCsvValidation';
import './Convert.css';


const Convert = () => {
  const [file, setFile] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setDownloadUrl('');
  };

  const handleProcess = async () => {
    if (!file) return;
    setProcessing(true);
    setDownloadUrl('');

    try {
      const { source, useWorker } = await prepareCsvSourceForParsing(file);
      const results = await parseCsv(source, useWorker);
      const fields = (results?.meta?.fields || []).map((h) => String(h ?? '').trim());
      const emailKey = detectEmailKey(fields);
      const entriesKey = detectEntriesKey(fields);

      if (!emailKey || !entriesKey) {
        alert('CSV must contain email and entries columns');
        return;
      }

      const groups = new Map();
      const data = Array.isArray(results?.data) ? results.data : [];

      data.forEach((rawRow) => {
        const row = sanitizeParsedRow(rawRow);
        if (!row) return;

        const email = normalizeEntrantKey(row[emailKey]);
        if (!email) return;

        const count = Math.max(0, toNumber(row[entriesKey]));
        if (!groups.has(email)) {
          const base = { ...row, [entriesKey]: count };
          groups.set(email, base);
          return;
        }

        const existing = groups.get(email);
        existing[entriesKey] = Math.max(0, toNumber(existing[entriesKey]) + count);
        mergeMissingIdentityFields(existing, row);
      });

      const aggregatedRows = Array.from(groups.values());
      const csv = Papa.unparse({
        fields,
        data: aggregatedRows,
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
    } catch (err) {
      alert('Error parsing CSV: ' + String(err?.message || err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="convert-page" style={{ padding: '1rem' }}>
      <h2>Combine Entry Row Lines</h2>
      <h6>Please use the exact csv file you downloaded from the Clean Sweeps Admin App</h6>
      <input type="file" accept=".csv" onChange={handleFileChange} />
      <button onClick={handleProcess} disabled={!file || processing} style={{ marginLeft: '1rem' }}>
        {processing ? 'Processing…' : 'Process CSV'}
      </button>

      {downloadUrl && (
        <div style={{ marginTop: '1rem' }}>
          <a href={downloadUrl} download="aggregated.csv">
            Download Aggregated CSV
          </a>
        </div>
      )}
    </div>
  );
};

function parseCsv(source, useWorker) {
  return new Promise((resolve, reject) => {
    Papa.parse(source, {
      header: true,
      skipEmptyLines: 'greedy',
      worker: useWorker,
      dynamicTyping: false,
      complete: (results) => resolve(results),
      error: (err) => reject(err || new Error('Failed to parse CSV.')),
    });
  });
}

function sanitizeParsedRow(data) {
  if (!data || typeof data !== 'object') return null;

  const out = {};
  let hasValue = false;

  Object.keys(data).forEach((rawKey) => {
    if (rawKey === '__parsed_extra__') return;
    const key = String(rawKey ?? '').trim();
    const value = String(data[rawKey] ?? '').trim();
    if (value) hasValue = true;
    out[key] = value;
  });

  return hasValue ? out : null;
}

function mergeMissingIdentityFields(target, source) {
  fillMissingField(target, source, ['first name', 'first_name', 'firstname', 'first']);
  fillMissingField(target, source, ['last name', 'last_name', 'lastname', 'last']);
  fillMissingField(target, source, [
    'name',
    'full_name',
    'fullname',
    'customer_name',
    'customername',
  ]);
}

function fillMissingField(target, source, candidates) {
  const sourceKey = findKeyByCandidates(source, candidates);
  if (!sourceKey || !hasValue(source[sourceKey])) return;

  const targetKey = findKeyByCandidates(target, candidates) || sourceKey;
  if (!hasValue(target[targetKey])) {
    target[targetKey] = source[sourceKey];
  }
}

function findKeyByCandidates(obj, candidates) {
  const map = new Map(Object.keys(obj || {}).map((k) => [normalizeKey(k), k]));
  for (const candidate of candidates) {
    const found = map.get(normalizeKey(candidate));
    if (found) return found;
  }
  return '';
}

function normalizeKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function hasValue(value) {
  return String(value || '').trim() !== '';
}

function toNumber(value) {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return 0;
  const parsed = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeEntrantKey(value) {
  return String(value || '').trim().toLowerCase();
}

export default Convert;
